/**
 * Custom Facebook Pixel Utility
 * Direct implementation without external packages
 *
 * Features:
 * - Single initialization per session
 * - Automatic deduplication
 * - Standard event tracking
 * - Custom event tracking
 * - Server-side ready (works with Conversion API)
 */

class FacebookPixelManager {
    constructor() {
        this.pixelId = null;
        this.initialized = false;
        this.queue = [];
        this.debug = false;

        // Track last events to prevent duplicates
        this.lastEvents = new Map();
        this.DUPLICATE_THRESHOLD = 5000; // 5 seconds
    }

    /**
     * Initialize Facebook Pixel
     * @param {string} pixelId - Facebook Pixel ID
     * @param {object} options - Configuration options
     */
    init(pixelId, options = {}) {
        if (!pixelId) {
            return;
        }

        // Check if already initialized
        const sessionKey = 'fb_pixel_initialized';
        const sessionPixelId = sessionStorage.getItem(sessionKey);

        if (this.initialized && this.pixelId === pixelId) {
            return;
        }

        if (sessionPixelId === pixelId) {
            this.pixelId = pixelId;

            // Even if in session, we need to ensure fbq is loaded
            if (!window.fbq) {
                this._injectPixelScript(pixelId);
            }

            this.initialized = true;
            this._processQueue();
            return;
        }

        this.pixelId = pixelId;
        this.debug = options.debug || false;

        console.log('🔵 [FB Pixel] Initialized:', pixelId);

        // Initialize fbq function
        this._injectPixelScript(pixelId);

        // Mark as initialized
        this.initialized = true;
        sessionStorage.setItem(sessionKey, pixelId);

        // Process queued events
        this._processQueue();
    }

    /**
     * Inject Facebook Pixel script
     * @private
     */
    _injectPixelScript(pixelId) {
        // The shop's server-rendered template (EtechStaticNginxSimpleUI/
        // controllers/html.controller.js) already injects the FB snippet
        // and calls fbq('init', pixelId) + fbq('track', 'PageView') in <head>.
        // If fbq is already present, just hook into it — calling init again
        // makes Meta log "Duplicate Pixel ID" and double-counts PageView.
        if (window.fbq) {
            return;
        }

        // Create fbq function
        // eslint-disable-next-line no-inner-declarations
        function fbq() {
            fbq.callMethod
                ? fbq.callMethod.apply(fbq, arguments)
                : fbq.queue.push(arguments);
        }

        if (!window._fbq) window._fbq = fbq;
        fbq.push = fbq;
        fbq.loaded = true;
        fbq.version = '2.0';
        fbq.queue = [];
        window.fbq = fbq;

        // Inject script tag
        const script = document.createElement('script');
        script.async = true;
        script.src = 'https://connect.facebook.net/en_US/fbevents.js';

        const firstScript = document.getElementsByTagName('script')[0];
        firstScript.parentNode.insertBefore(script, firstScript);

        // Initialize pixel
        window.fbq('init', pixelId);
    }

    /**
     * Process queued events
     * @private
     */
    _processQueue() {
        if (this.queue.length === 0) {
            return;
        }

        while (this.queue.length > 0) {
            const event = this.queue.shift();
            this._trackEvent(event.name, event.data, event.options);
        }
    }

    /**
     * Check if event is duplicate
     * @private
     */
    _isDuplicate(eventName, data) {
        const eventKey = `${eventName}-${JSON.stringify(data)}`;
        const lastTime = this.lastEvents.get(eventKey);
        const now = Date.now();

        if (lastTime && (now - lastTime) < this.DUPLICATE_THRESHOLD) {
            console.warn('🚫 [FB Pixel] DUPLICATE PREVENTED:', eventName);
            return true;
        }

        this.lastEvents.set(eventKey, now);

        // Cleanup old entries
        if (this.lastEvents.size > 100) {
            const firstKey = this.lastEvents.keys().next().value;
            this.lastEvents.delete(firstKey);
        }

        return false;
    }

    /**
     * Track standard or custom event
     * @private
     */
    _trackEvent(eventName, data = {}, options = {}) {
        if (!this.initialized) {
            this.queue.push({ name: eventName, data, options });
            return;
        }

        if (!window.fbq) {
            return;
        }

        // Check for duplicates (except PageView)
        if (eventName !== 'PageView' && this._isDuplicate(eventName, data)) {
            return;
        }

        try {
            window.fbq('track', eventName, data, options);

            // Log pixel events
            const timestamp = new Date().toLocaleTimeString();
            console.log(`✅ [FB Pixel] ${timestamp} - ${eventName}`, {
                ...data,
                ...options
            });
        } catch (error) {
            console.error('❌ [FB Pixel] Track error:', error);
        }
    }

    /**
     * Track PageView event
     */
    pageView() {
        this._trackEvent('PageView');
    }

    /**
     * Track ViewContent event
     * @param {object} data - Event data
     */
    viewContent(data = {}) {
        this._trackEvent('ViewContent', {
            content_name: data.content_name || '',
            content_ids: data.content_ids || [],
            content_type: data.content_type || 'product',
            value: data.value || 0,
            currency: data.currency || 'BDT'
        });
    }

    /**
     * Track AddToCart event
     * @param {object} data - Event data
     */
    addToCart(data = {}) {
        this._trackEvent('AddToCart', {
            content_ids: data.content_ids || [],
            content_name: data.content_name || '',
            content_type: data.content_type || 'product',
            value: data.value || 0,
            currency: data.currency || 'BDT'
        });
    }

    /**
     * Track InitiateCheckout event
     * @param {object} data - Event data
     */
    initiateCheckout(data = {}) {
        this._trackEvent('InitiateCheckout', {
            content_ids: data.content_ids || [],
            num_items: data.num_items || 0,
            value: data.value || 0,
            currency: data.currency || 'BDT'
        });
    }

    /**
     * Track Purchase event
     * @param {object} data - Event data
     */
    purchase(data = {}) {
        // Generate unique event_id for deduplication with Conversion API
        const eventId = data.event_id || `purchase_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;

        console.log('🛒 [FB Pixel] PURCHASE:', {
            eventId,
            value: data.value || 0,
            content_ids: data.content_ids || []
        });

        this._trackEvent('Purchase', {
            content_ids: data.content_ids || [],
            content_type: data.content_type || 'product',
            value: data.value || 0,
            currency: 'USD',
            num_items: data.num_items || 0
        }, {
            eventID: eventId // This helps deduplicate with Conversion API
        });

        return eventId;
    }

    /**
     * Track custom event
     * @param {string} eventName - Custom event name
     * @param {object} data - Event data
     */
    trackCustom(eventName, data = {}) {
        if (!eventName) {
            return;
        }
        this._trackEvent(eventName, data);
    }

    /**
     * Track standard event (generic)
     * @param {string} eventName - Standard event name
     * @param {object} data - Event data
     * @param {object} options - Additional options
     */
    track(eventName, data = {}, options = {}) {
        this._trackEvent(eventName, data, options);
    }

    /**
     * Enable/disable debug mode
     * @param {boolean} enabled - Debug mode
     */
    setDebug(enabled) {
        this.debug = enabled;
    }

    /**
     * Get current pixel ID
     */
    getPixelId() {
        return this.pixelId;
    }

    /**
     * Check if initialized
     */
    isInitialized() {
        return this.initialized;
    }

    /**
     * Clear event history (for testing)
     */
    clearEventHistory() {
        this.lastEvents.clear();
    }
}

// Create singleton instance
const fbPixel = new FacebookPixelManager();

// Export functions
export const initFacebookPixel = (pixelId, options) => fbPixel.init(pixelId, options);
export const fbPageView = () => fbPixel.pageView();
export const fbViewContent = (data) => fbPixel.viewContent(data);
export const fbAddToCart = (data) => fbPixel.addToCart(data);
export const fbInitiateCheckout = (data) => fbPixel.initiateCheckout(data);
export const fbPurchase = (data) => fbPixel.purchase(data);
export const fbTrackCustom = (eventName, data) => fbPixel.trackCustom(eventName, data);
export const fbTrack = (eventName, data, options) => fbPixel.track(eventName, data, options);
export const fbSetDebug = (enabled) => fbPixel.setDebug(enabled);

// Export instance for advanced usage
export default fbPixel;

// Global console helper for debugging
if (typeof window !== 'undefined') {
    window.fbPixelDebug = {
        status: () => {
            console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
            console.log('📊 FB PIXEL STATUS');
            console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
            console.log('Initialized:', fbPixel.isInitialized());
            console.log('Pixel ID:', fbPixel.getPixelId() || 'Not set');
            console.log('window.fbq exists:', typeof window.fbq !== 'undefined');
            console.log('Queue length:', fbPixel.queue.length);
            console.log('Event history size:', fbPixel.lastEvents.size);
            console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        },
        clear: () => {
            fbPixel.clearEventHistory();
            console.log('✅ Event history cleared');
        },
        history: () => {
            console.log('📋 Event History (last', fbPixel.lastEvents.size, 'events):');
            fbPixel.lastEvents.forEach((timestamp, key) => {
                const timeDiff = Date.now() - timestamp;
                console.log(`  ${key} - ${timeDiff}ms ago`);
            });
        }
    };
}
